import { IPromise } from 'angular';
import * as _ from 'underscore';

import { CheckBoxWithLabelProps, DialogType } from '@tableau/widgets';
import React = require('react');
import { UpdateGrantLicenseModeDialog } from '../../app/groups/UpdateGrantLicenseModeDialog';
import { Messages } from '../../codegen/Localize';
import { CommonStyles } from '../../common/react/CommonStyles';
import { DialogAlertProps } from '../../common/react/components/dialogAlerts/DialogAlerts';
import { AngularContextFactory } from '../factories/AngularContextFactory';
import { FeatureFlags } from '../FeatureFlags';
import { ISiteRoleFilter } from '../filters/SiteRoleFilter';
import { IActionContext } from '../helpers/ActionContext';
import { ResultHelper } from '../helpers/ResultHelper';
import { ISiteRolePickerMenuScope, SiteRolePickerMenuHelper } from '../helpers/SiteRolePickerMenuHelper';
import { L10n } from '../L10n/L10n';
import { IGroupDetailsInfo } from '../models/IGroupDetailsInfo';
import { IGroupBasicInfo, IGroupInfo } from '../models/IGroupInfo';
import { Set } from '../models/Set';
import { Module } from '../Module';
import { Groups } from '../resources/Groups';
import { SiteRoleOption, SiteRoleOptionsResource } from '../../userManagement/services/resources/SiteRoleOptions';
import { GrantLicenseModes } from '../ServerApi/requests/SyncActiveDirectoryGroupRequest';
import { IResult } from '../ServerApi/types/IResponse';
import { SiteRoles } from '../ServerApi/types/ISiteRole';
import { ConfirmActionDialog, IConfirmActionDialogInstance } from './ConfirmActionDialog';
import { ModalInstance, ReactModalService } from './ReactModalService';
import { ServerService } from './ServerService';
import { ToasterService, ToastType } from './ToasterService';
import { ISetGrantLicenseModeLocalGroupsDialogScope } from './UpdateLocalGroupGrantLicenseModeAction';

export interface ISetGroupsMinimumSiteRoleDialogScope extends ISiteRolePickerMenuScope {
  description: string;
  siteRole: string;
  siteRoleOptions: string[];
  setSiteRole: (role: string) => void;
  licenseUponLoginCheckboxProps: CheckBoxWithLabelProps;
  grantLicenseMode: GrantLicenseModes;
  dialogInfoAlertProps: DialogAlertProps;
  isUnlicensed: boolean;
}

const groupUpdateDialogStyle: React.CSSProperties = {
  width: 'auto',
  height: 'auto',
  paddingTop: '18px',
  paddingBottom: '12px',
  paddingLeft: '12px',
  paddingRight: '12px'
};

export class UpdateGroupsMinimumSiteRoleAction {

  public static $inject = [
    '$translate',
    '$q',
    '$filter',
    'Groups',
    'AngularContextFactory'
  ];

  constructor(
    private $translate: ng.translate.ITranslateService,
    private $q: ng.IQService,
    private $filter: ng.IFilterService,
    private Groups: Groups,
    private angularContextFactory: AngularContextFactory
  ) { }

  public executeInPlace(group: IGroupDetailsInfo, siteRole: string, grantLicenseMode: GrantLicenseModes) {
    const groupSet = Set.fromIdItemArray([group]);

    return this.Groups.updateGroupsMinimumSiteRole([group.id], siteRole, grantLicenseMode)
      .then(ResultHelper.rejectResultOnError(this.$q))
      .then(() => this.handleSuccessResult(groupSet, siteRole))
      .catch((result: IResult) => this.handleErrorResult(groupSet, siteRole, result));
  }

  public showUpdateGroupsMinimumSiteRoleDialog(
    groupsToSet: Set<IGroupInfo>,
    context: IActionContext): IPromise<any> {

    let modalInstance: ModalInstance;
    const dialogScope = <ISetGrantLicenseModeLocalGroupsDialogScope> context.scope.$new();

    const dialogProps = {
      angularContext: this.angularContextFactory.create(dialogScope),
      dismiss: () => modalInstance && modalInstance.dismiss('cancel'),
      close: (result: any) => modalInstance && modalInstance.close(result),
      groupsToSet: groupsToSet,
      isActiveDirectory: true
    };

    modalInstance = ReactModalService.open({
      type: DialogType.Uber,
      dialogContent: React.createElement(UpdateGrantLicenseModeDialog, dialogProps),
      testId: 'updateLocalGroupGrantLicenseMode',
      showCloseButton: true,
      dialogStyle: groupUpdateDialogStyle,
      shouldNotSetDefaultWidth: false
    });

    modalInstance.result.finally(() => dialogScope.$destroy());

    return modalInstance.result;
  }


  private handleSuccessResult(groups: Set<IGroupBasicInfo>, siteRole: string) {
    ToasterService.instance.pop({
      type: ToastType.Info,
      text: this.$translate.instant('updateGroupsSiteRoleSettingsSuccess', {
        GROUP_NAME: L10n.elideIfLong(groups.first().name),
        COUNT: groups.size(),
      }, L10n.Interpolations.Count)
    });
  }

  private handleErrorResult(groups: Set<IGroupBasicInfo>, siteRole: string, result: IResult) {
    ToasterService.instance.pop({
      type: ToastType.Error,
      text: this.$translate.instant('updateGroupsMinimumSiteRoleError', {
        GROUP_NAME: L10n.elideIfLong(groups.first().name),
        COUNT: groups.size(),
        SITE_ROLE: this.$filter<ISiteRoleFilter>('siteRole')(siteRole)
      }, L10n.Interpolations.Count)
    });
    return this.$q.reject(result); // for chaining errors
  }

}

Module.instance().service('UpdateGroupsMinimumSiteRoleAction', UpdateGroupsMinimumSiteRoleAction);
